import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { ArrowLeft, ExternalLink, Mail, MapPin, Globe, ShieldCheck, Star, Building2, Linkedin, Youtube, Twitter, ChevronRight } from "lucide-react";
import api from "@/api";
import { ProviderLogo, VerifiedBadge, FeaturedBadge, TierBadge } from "@/components/ProviderCard";
import Seo from "@/components/Seo";

function SpecBlock({ title, items, mono = false }) {
  if (!items || items.length === 0) return null;
  return (
    <div className="border-t border-slate-200 pt-6">
      <div className="text-xs font-semibold uppercase tracking-widest text-slate-500 mb-3">{title}</div>
      <div className="flex flex-wrap gap-1.5">
        {items.map((it) => (
          <span key={it} className={`tag ${mono ? "font-mono text-[11px]" : ""} tag-tech`}>{it}</span>
        ))}
      </div>
    </div>
  );
}

export default function ProviderProfile() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setLoading(true);
    api.get(`/providers/slug/${slug}`)
      .then(({ data }) => {
        setData(data);
        api.post("/analytics/event", { event_type: "provider_profile_viewed", provider_id: data.provider.id }).catch(()=>{});
      })
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [slug]);

  if (loading) return <div className="max-w-7xl mx-auto px-6 py-20 text-slate-500">Loading provider…</div>;
  if (notFound) return (
    <div className="max-w-7xl mx-auto px-6 py-24 text-center">
      <h1 className="font-display font-bold text-3xl">Provider not found</h1>
      <p className="mt-2 text-slate-500">This listing may have been removed.</p>
      <Link to="/solutions" className="btn-primary mt-6 inline-flex">Back to directory</Link>
    </div>
  );

  const p = data.provider;
  const similar = data.similar || [];
  // Public display gated by explicit admin toggles, NOT tier alone.
  const showVerifiedBadge = p.show_verified_badge === true;
  const showFeaturedBadge = p.show_featured_badge === true;
  const canRequestQuote = p.enable_quote_button === true;
  const showGallery = p.enable_gallery === true && (p.images || []).length > 0;
  const showVideo = p.enable_video === true && !!p.video_url;
  const showCaseStudy = p.enable_case_studies === true && !!p.case_study_url;
  const showContactEmail = p.show_contact_details === true && !!p.contact_email;
  const isBasic = !showVerifiedBadge && !showFeaturedBadge && p.tier === "Basic";

  return (
    <div className="bg-slate-50 min-h-screen">
      <Seo
        title={p.seo_title || p.name}
        absoluteTitle={!!p.seo_title}
        description={
          p.seo_description ||
          p.tagline ||
          p.summary ||
          `${p.name} — tracking solution provider${p.hq_country ? " based in " + p.hq_country : ""}. View specs, technical capabilities, compliance and request a quote via Strategic Tracking.`
        }
        canonical={p.canonical_url || undefined}
        noindex={!!p.noindex}
        ogImage={p.og_image_override || `${process.env.REACT_APP_BACKEND_URL}/api/og/provider/${p.slug}.png`}
        ogType="profile"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "Organization",
          "name": p.name,
          "url": p.website || undefined,
          "logo": p.logo_url || undefined,
          "description": p.tagline || p.summary || undefined,
          "address": p.hq_country
            ? {
                "@type": "PostalAddress",
                "addressLocality": p.hq_city || undefined,
                "addressCountry": p.hq_country,
              }
            : undefined,
          "sameAs": [p.linkedin_url, p.twitter_url, p.youtube_url].filter(Boolean),
        }}
      />
      {/* Breadcrumb */}
      <div className="border-b border-slate-200 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3 text-sm flex items-center gap-2 text-slate-500" data-testid="breadcrumb">
          <Link to="/" className="hover:text-slate-900">Home</Link>
          <ChevronRight size={14}/>
          <Link to="/solutions" className="hover:text-slate-900">Solutions</Link>
          <ChevronRight size={14}/>
          <span className="text-slate-900 truncate">{p.name}</span>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <button onClick={() => navigate(-1)} className="text-sm text-slate-500 hover:text-slate-900 inline-flex items-center gap-1 mb-6" data-testid="back-button">
          <ArrowLeft size={14}/> Back
        </button>

        {/* Header card */}
        <div className="card-elevated p-8" data-testid="provider-header">
          <div className="flex flex-col md:flex-row gap-6 md:items-start">
            <ProviderLogo provider={p} size={88}/>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap mb-2">
                {showVerifiedBadge && <VerifiedBadge/>}
                {showFeaturedBadge && <FeaturedBadge/>}
                <TierBadge tier={p.tier}/>
                {isBasic && <span className="tag">Basic listing</span>}
              </div>
              <h1 className="font-display font-extrabold text-3xl sm:text-4xl tracking-tight text-slate-900" data-testid="provider-name">{p.name}</h1>
              {p.tagline && <p className="mt-2 text-lg text-slate-600 max-w-3xl" data-testid="provider-tagline">{p.tagline}</p>}
              {p.summary && <p className="mt-3 text-slate-700 leading-relaxed max-w-3xl" data-testid="provider-summary">{p.summary}</p>}
              <div className="mt-4 flex flex-wrap gap-x-5 gap-y-2 text-sm text-slate-600">
                {(p.hq_city || p.hq_country) && (
                  <div className="inline-flex items-center gap-1.5"><MapPin size={14}/> {[p.hq_city, p.hq_country].filter(Boolean).join(", ")}</div>
                )}
                {p.website && (
                  <a href={p.website} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-[#002FA7] hover:underline" data-testid="provider-website-link"
                    onClick={() => api.post("/analytics/event", { event_type: "provider_website_clicked", provider_id: p.id }).catch(()=>{})}>
                    <Globe size={14}/> Visit website <ExternalLink size={12}/>
                  </a>
                )}
                {p.founded_year && (
                  <div className="inline-flex items-center gap-1.5"><Building2 size={14}/> Founded {p.founded_year}</div>
                )}
              </div>
              <div className="mt-5 flex flex-wrap gap-3">
                {canRequestQuote && (
                  <Link to={`/request-quotes?provider=${p.slug}`} className="btn-accent" data-testid="header-request-quote">
                    <Mail size={16}/> Request quote
                  </Link>
                )}
                {!canRequestQuote && (
                  <Link to={`/request-quotes?provider=${p.slug}`} className="btn-secondary" data-testid="header-quote-via-st">
                    Request quote via Strategic Tracking
                  </Link>
                )}
                {p.claim_status !== "Claimed" && (
                  <Link to={`/claim-listing/${p.slug}`} className="btn-primary" data-testid="header-claim">
                    <ShieldCheck size={16}/> Claim this listing
                  </Link>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="grid lg:grid-cols-[1fr_320px] gap-8 mt-8">
          {/* Main column */}
          <div className="card-elevated p-8">
            {p.description && (
              <div>
                <div className="text-xs font-semibold uppercase tracking-widest text-slate-500 mb-3">Overview</div>
                <p className="text-slate-700 leading-relaxed whitespace-pre-line" data-testid="provider-description">{p.description}</p>
              </div>
            )}

            <SpecBlock title="Provider type" items={p.provider_types}/>
            <SpecBlock title="Solution categories" items={p.solution_categories}/>
            <SpecBlock title="Device / product types" items={p.device_types}/>
            <SpecBlock title="Connectivity & positioning" items={p.connectivity} mono/>
            <SpecBlock title="Sensors & condition monitoring" items={p.sensors}/>
            <SpecBlock title="Battery & power model" items={p.battery_life}/>
            <SpecBlock title="Commercial model" items={p.commercial_models}/>
            <SpecBlock title="Compliance & certification" items={p.compliance}/>
            <SpecBlock title="Industries served" items={p.industries}/>
            <SpecBlock title="Use cases" items={p.use_cases}/>
            <SpecBlock title="Integration capabilities" items={p.integration_capabilities}/>
            {p.regions_served?.length > 0 && <SpecBlock title="Regions served" items={p.regions_served}/>}

            {showGallery && (
              <div className="border-t border-slate-200 pt-6 mt-6">
                <div className="text-xs font-semibold uppercase tracking-widest text-slate-500 mb-3">Gallery</div>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  {(p.images || []).slice(0, p.max_gallery_images || 8).map((src, i) => (
                    <img key={i} src={src} alt="" className="rounded-md border border-slate-200 aspect-video object-cover"/>
                  ))}
                </div>
              </div>
            )}

            {showVideo && (
              <div className="border-t border-slate-200 pt-6 mt-6">
                <div className="text-xs font-semibold uppercase tracking-widest text-slate-500 mb-3">Video</div>
                <a href={p.video_url} target="_blank" rel="noopener noreferrer" className="text-[#002FA7] hover:underline inline-flex items-center gap-1">
                  Watch video <ExternalLink size={13}/>
                </a>
              </div>
            )}

            {showCaseStudy && (
              <div className="border-t border-slate-200 pt-6 mt-6">
                <div className="text-xs font-semibold uppercase tracking-widest text-slate-500 mb-3">Case study</div>
                <a href={p.case_study_url} target="_blank" rel="noopener noreferrer" className="text-[#002FA7] hover:underline inline-flex items-center gap-1">
                  View case study <ExternalLink size={13}/>
                </a>
              </div>
            )}
          </div>

          {/* Sidebar */}
          <aside className="space-y-4">
            <div className="card-elevated p-5 sticky top-20">
              <div className="text-xs font-semibold uppercase tracking-widest text-slate-500 mb-3">Get in touch</div>
              {canRequestQuote ? (
                <>
                  <Link to={`/request-quotes?provider=${p.slug}`} className="btn-accent w-full justify-center" data-testid="sidebar-request-quote">
                    Request quote
                  </Link>
                  <p className="text-xs text-slate-500 mt-2">
                    We'll route your enquiry directly to this provider.
                  </p>
                </>
              ) : (
                <>
                  <Link to={`/request-quotes?provider=${p.slug}`} className="btn-primary w-full justify-center" data-testid="sidebar-request-via-st">
                    Request quote via Strategic Tracking
                  </Link>
                  <p className="text-xs text-slate-500 mt-2">
                    {p.claim_status === "Claimed"
                      ? "Our team will qualify your enquiry and forward it to this provider."
                      : "This listing is unclaimed — our team will qualify and forward your enquiry."}
                  </p>
                </>
              )}
              {showContactEmail && (
                <a href={`mailto:${p.contact_email}`} className="mt-3 block text-sm text-[#002FA7] hover:underline">
                  {p.contact_email}
                </a>
              )}
              {(p.linkedin_url || p.youtube_url || p.twitter_url) && (
                <div className="mt-4 flex gap-3 text-slate-500">
                  {p.linkedin_url && <a href={p.linkedin_url} target="_blank" rel="noopener noreferrer" className="hover:text-slate-900"><Linkedin size={16}/></a>}
                  {p.youtube_url && <a href={p.youtube_url} target="_blank" rel="noopener noreferrer" className="hover:text-slate-900"><Youtube size={16}/></a>}
                  {p.twitter_url && <a href={p.twitter_url} target="_blank" rel="noopener noreferrer" className="hover:text-slate-900"><Twitter size={16}/></a>}
                </div>
              )}
            </div>

            {p.claim_status !== "Claimed" && (
              <div className="card-elevated p-5 bg-slate-900 text-white border-slate-900">
                <div className="text-xs font-semibold uppercase tracking-widest text-orange-300 mb-2">Are you {p.name}?</div>
                <p className="text-sm text-slate-200">Claim this listing to verify your details, route leads directly to your team and unlock provider analytics.</p>
                <Link to={`/claim-listing/${p.slug}`} className="mt-4 inline-flex items-center gap-2 bg-white text-slate-900 font-semibold px-4 py-2 rounded-md text-sm">
                  Claim listing
                </Link>
              </div>
            )}
          </aside>
        </div>

        {/* Similar */}
        {similar.length > 0 && (
          <div className="mt-12">
            <div className="text-xs font-semibold uppercase tracking-widest text-slate-500 mb-3">Similar providers</div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {similar.map((s) => (
                <Link key={s.id} to={`/solutions/${s.slug}`} className="card-elevated p-4 block" data-testid={`similar-${s.slug}`}>
                  <div className="flex items-center gap-3">
                    <ProviderLogo provider={s} size={40}/>
                    <div className="min-w-0">
                      <div className="font-display font-semibold text-slate-900 truncate">{s.name}</div>
                      <div className="text-xs text-slate-500">{s.hq_country}</div>
                    </div>
                  </div>
                  <p className="mt-2 text-xs text-slate-600 line-clamp-2">{s.tagline}</p>
                </Link>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
